import { useRef, useEffect, useState } from 'react'

interface AddressParts {
  addressLine1: string
  city: string
  state: string
  zipCode: string
}

interface AddressAutocompleteProps {
  label: React.ReactNode
  value: string
  stateRestriction?: string
  onChange: (value: string) => void
  onAddressSelect?: (parts: AddressParts) => void
  placeholder?: string
  delay?: number
  error?: boolean
  disabled?: boolean
}

export function AddressAutocomplete({
  label,
  value,
  stateRestriction = '',
  onChange,
  onAddressSelect,
  placeholder = 'Full Address',
  delay = 0,
  error = false,
  disabled = false
}: AddressAutocompleteProps) {
  const inputRef = useRef<HTMLInputElement>(null)
  const autocompleteRef = useRef<google.maps.places.Autocomplete | null>(null)
  const [isFocused, setIsFocused] = useState(false)
  const [inputValue, setInputValue] = useState(value)
  const onAddressSelectRef = useRef(onAddressSelect)
  const normalizedStateRestriction = stateRestriction.trim().toUpperCase()
  const [mapsReady, setMapsReady] = useState(
    typeof window !== 'undefined' &&
      typeof google !== 'undefined' &&
      Boolean(google.maps?.places?.Autocomplete)
  )
  const [restrictionBounds, setRestrictionBounds] = useState<google.maps.LatLngBounds | null>(null)
  const [isRestrictionResolved, setIsRestrictionResolved] = useState(
    () => normalizedStateRestriction === ''
  )

  useEffect(() => {
    onAddressSelectRef.current = onAddressSelect
  }, [onAddressSelect])

  // Sync external value changes (e.g. pre-population from stored data)
  useEffect(() => {
    setInputValue(value)
  }, [value])

  useEffect(() => {
    if (mapsReady) return

    const handleReady = () => setMapsReady(true)
    window.addEventListener('google-maps-ready', handleReady)

    return () => {
      window.removeEventListener('google-maps-ready', handleReady)
    }
  }, [mapsReady])

  useEffect(() => {
    if (!mapsReady || !normalizedStateRestriction) {
      setRestrictionBounds(null)
      setIsRestrictionResolved(true)
      return
    }

    if (typeof google === 'undefined' || !google.maps?.Geocoder) {
      return
    }

    let cancelled = false
    setIsRestrictionResolved(false)

    const geocoder = new google.maps.Geocoder()
    geocoder.geocode(
      {
        address: `${normalizedStateRestriction}, US`
      },
      (results, status) => {
        if (cancelled) {
          return
        }

        const viewport = status === 'OK' ? results?.[0]?.geometry?.viewport ?? null : null
        setRestrictionBounds(viewport)
        setIsRestrictionResolved(true)
      }
    )

    return () => {
      cancelled = true
    }
  }, [mapsReady, normalizedStateRestriction])

  useEffect(() => {
    if (
      disabled ||
      !mapsReady ||
      !isRestrictionResolved ||
      !inputRef.current ||
      typeof google === 'undefined' ||
      !google.maps?.places?.Autocomplete
    ) {
      return
    }

    const autocomplete = new google.maps.places.Autocomplete(inputRef.current, {
      bounds: restrictionBounds ?? undefined,
      strictBounds: Boolean(restrictionBounds),
      types: ['address'],
      componentRestrictions: { country: 'us' },
      fields: ['address_components', 'formatted_address']
    })
    autocompleteRef.current = autocomplete

    const listener = autocomplete.addListener('place_changed', () => {
      const place = autocomplete.getPlace()
      if (!place.address_components) return

      let streetNumber = ''
      let route = ''
      let city = ''
      let state = ''
      let zipCode = ''

      for (const component of place.address_components) {
        const types = component.types
        if (types.includes('street_number')) streetNumber = component.long_name
        else if (types.includes('route')) route = component.long_name
        else if (types.includes('locality')) city = component.long_name
        else if (!city && types.includes('sublocality_level_1')) city = component.long_name
        else if (!city && types.includes('sublocality')) city = component.long_name
        else if (!city && types.includes('administrative_area_level_3')) city = component.long_name
        else if (types.includes('administrative_area_level_1')) state = component.short_name
        else if (types.includes('postal_code')) zipCode = component.long_name
      }

      const addressLine1 = [streetNumber, route].filter(Boolean).join(' ')
      const displayAddress = place.formatted_address || addressLine1
      setInputValue(displayAddress)
      onChange(displayAddress)
      onAddressSelectRef.current?.({ addressLine1, city, state, zipCode })
    })

    return () => {
      google.maps.event.removeListener(listener)
      autocompleteRef.current = null
    }
  }, [
    disabled,
    isRestrictionResolved,
    mapsReady,
    normalizedStateRestriction,
    restrictionBounds
  ]) // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="animate-fade-in-up" style={{ animationDelay: `${delay}ms` }}>
      <label className="enrollment-field-label">{label}</label>
      <input
        ref={inputRef}
        type="text"
        value={inputValue}
        disabled={disabled}
        onChange={e => {
          setInputValue(e.target.value)
          onChange(e.target.value)
        }}
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
        placeholder={isFocused ? '' : placeholder}
        className={`enrollment-field-control ${error ? 'enrollment-field-control--error' : ''}`}
      />
    </div>
  )
}
